/**
 * The moments the game answers to, and the only list of them.
 *
 * A cue is not a vibration and not a sound - it is a thing that happened: the
 * turn arriving, a guess refused, the code going. How that reaches the player
 * is a channel's business. Keeping the moments here rather than inside one
 * channel is what stops two renderings of the same event from drifting apart,
 * and lets a test insist that every channel covers every moment instead of
 * discovering a missing one when a player finally triggers it.
 *
 * Weight is how big the news is, on a scale the whole app shares. It is not a
 * duration and not a volume - each channel spends it in its own currency. It
 * exists because both channels need to rank cues against each other and
 * neither should be guessing from how long the other one's pattern happens to
 * run.
 */

/**
 * Every moment in the app, and the only place to rank one.
 *
 * The ranks are coarse on purpose. Cues that share a weight are alternatives to
 * each other - a score is exactly one of dead, near or close - so the ordering
 * only ever has to hold between the groups.
 */
export const ACTIONS = {
    /** A peg into a socket. Five of these to a guess, so it is the floor. */
    peg: { weight: 1 },
    copied: { weight: 1 },

    /** The fifth peg. The row can be sent now, and says so by weighing more. */
    full: { weight: 2 },
    /** Taking a peg back. Undoing is not doing, but it is still an answer. */
    clear: { weight: 2 },

    /** A hold reaching the point where letting go would do something. */
    hold: { weight: 3 },
    /** Scored 0/0. */
    dead: { weight: 3 },
    /** Colours found, none of them placed. */
    near: { weight: 3 },
    /** Something in the right place, which is the only score that rises. */
    close: { weight: 3 },

    /** The turn coming to you. */
    turn: { weight: 4 },
    /** A reaction from the opponent - the only message this game carries. */
    poke: { weight: 4 },
    /** The server refused the guess. */
    refused: { weight: 4 },

    /** The opponent got there first. */
    lost: { weight: 5 },
    /** Giving a game up. */
    forfeit: { weight: 5 },

    /** The code going. Nothing outranks it and nothing follows it. */
    win: { weight: 6 },
};

/** How big a moment is. Throws rather than ranking a name nothing defines. */
export function weightOf(name) {
    const action = ACTIONS[name];
    if (!action) throw new Error(`cue: no action named ${name}`);
    return action.weight;
}

/**
 * The one way anything in the app says something happened.
 *
 * Every channel is offered every cue and decides for itself - a channel that is
 * off, unavailable, or already busy answers false and the others carry on. The
 * dispatcher deliberately holds no suppression rule of its own: what a motor
 * can do at once and what a mixer can do at once are different questions, and
 * answering them here would apply one device's limits to the other.
 *
 * The name is checked once, here, so a typo is a thrown error at the call site
 * rather than a cue that quietly stops existing on every channel at once.
 */
export function createCues(channels = []) {
    return function cue(name) {
        weightOf(name);

        let reached = false;
        for (const channel of channels) {
            if (reaches(channel, name)) reached = true;
        }
        return reached;
    };
}

/**
 * Whether one channel got the cue to the player.
 *
 * A channel that throws is a channel that did not reach anybody, which is the
 * same answer as one that is off - and it must be the same answer, because a
 * cue is decoration and the calls around it are not. Cueing happens on the way
 * to closing a sheet and on the way to giving a game up; an exception out of
 * here used to leave the sheet open and the game unforfeited, which is the
 * audio hardware deciding whether the app works.
 *
 * The channels are the far end of the browser - a motor, a mixer, a permission
 * - and none of them is a place to be sure nothing can fail. The one error
 * that still travels is the unknown cue name, thrown by weightOf before any
 * channel is asked: that is a typo in our own source, and silencing it would
 * silence the moment everywhere at once.
 */
function reaches(channel, name) {
    try {
        return channel.enabled() ? Boolean(channel.play(name)) : false;
    } catch {
        return false;
    }
}
